import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useFetcher } from "@remix-run/react";

type TickerSearchModalProps = {
    showSearchModal: boolean;
    setShowSearchModal: (showSearchModal: boolean) => void;
    title?: string;
    mode: "financials" | "snapshots" | "insider-activity" | "news" | null | undefined;
};

const modeConfig: Record<string, { mode: "financials" | "snapshots" | "insider-activity" | "news" | null, title: string; placeholder: string }> = {
    financials: {
        mode: "financials",
        title: "Jump to Financials",
        placeholder: "Select Ticker"
    },
    snapshots: {
        mode: "snapshots",
        title: "Jump to Snapshots",
        placeholder: "Select Ticker"
    },
    "insider-activity": {
        mode: "insider-activity",
        title: "Jump to Insider Activity",
        placeholder: "Select Ticker"
    },
    news: {
        mode: "news",
        title: "Jump to News & Sentiment",
        placeholder: "Select Ticker"
    }
    // Add more modes here as needed
};

const TickerSearchModal: React.FC<TickerSearchModalProps> = ({
    showSearchModal,
    setShowSearchModal,
    mode
}) => {

    let title = "Search Your Ticker";
    const fetcher = useFetcher();
    const [suggestions, setSuggestions] = useState<Array<{
        ticker_symbol: string;
        name: string;
        market_cap: number;
        logo_url: string;
        ticker_id: string;
        exchange: string;
        company_id: string;
    }>>([]);
    const inputRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const navigate = useNavigate();
    const [isFocused, setIsFocused] = useState(false);

    // Determine title and placeholder based on mode
    const dynamicTitle = mode && modeConfig[mode]?.title ? modeConfig[mode].title : (title || "Search");
    const dynamicPlaceholder = mode && modeConfig[mode]?.placeholder ? modeConfig[mode].placeholder : "Search for a stock...";

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev > 0 ? prev - 1 : -1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0) {
                    handleSelectStock(suggestions[activeIndex]);
                }
                break;
            case 'Escape':
                setSuggestions([]);
                setActiveIndex(-1);
                break;
        }
    };

    const handleSelectStock = (stock: { ticker_id: string; exchange: string; ticker_symbol: string; company_id: string; }) => {
        console.log(stock);
        setSuggestions([]);
        setActiveIndex(-1);
        setShowSearchModal(false);

        const params = new URLSearchParams({
            type: "company",
            cid: stock.company_id,
        });
        if (mode) {
            params.append("active-panel", mode);
        }
        navigate(
            `/ticker/${stock.ticker_id}/${stock.exchange}/${stock.ticker_symbol}?${params.toString()}`,
            { replace: false, state: { needRefresh: true } }
        );
    };

    const handleSearch = debounce(async (value: string) => {
        if (!value) {
            setSuggestions([]);
            return;
        }

        fetcher.load(`/api/tickers?search_term=${encodeURIComponent(value)}`);
    }, 300);

    useEffect(() => {
        if (fetcher.data && Array.isArray(fetcher.data)) {
            setSuggestions(fetcher.data.slice(0, 5));
        }
    }, [fetcher.data]);

    useEffect(() => {
        if (showSearchModal && inputRef.current) {
            (inputRef.current as HTMLInputElement).focus();
        }
    }, [showSearchModal]);

    return (
        <div className="search-modal">
            <Dialog open={showSearchModal} onClose={() => { }} className="relative z-30">
                <DialogBackdrop
                    className="fixed inset-0 bg-gray-500/75"
                />

                <div className="fixed inset-0 w-screen overflow-y-auto pt-20">
                    <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-start sm:p-0">
                        <DialogPanel
                            className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 w-[95%] sm:w-full sm:max-w-lg sm:p-6"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-semibold text-gray-900">{dynamicTitle}</h2>
                                <button
                                    type="button"
                                    onClick={() => setShowSearchModal(false)}
                                    className="rounded-md bg-white text-gray-900 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    <FontAwesomeIcon icon={faCircleXmark} className="size-6" />
                                </button>
                            </div>
                            <div className="mt-4 text-center sm:ml-4 sm:text-left">
                                <div className="relative">
                                    <input
                                        ref={inputRef}
                                        autoFocus={true}
                                        tabIndex={0}
                                        type="text"
                                        id="search-bar-modal-text"
                                        className="w-full px-4 py-3 text-gray-900 border border-gray-300 rounded-full focus:ring-2 focus:ring-green-900 focus:border-transparent pr-10"
                                        placeholder={dynamicPlaceholder}
                                        onKeyDown={handleKeyDown}
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => {
                                            setTimeout(() => {
                                                setIsFocused(false);
                                                setActiveIndex(-1);
                                            }, 200);
                                        }}
                                        onChange={(e) => {
                                            const value = e.target.value.toLowerCase();
                                            handleSearch(value);
                                            setActiveIndex(-1);
                                        }}
                                        autoComplete='off'
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400 size-5" />
                                    </div>
                                    {suggestions.length > 0 && isFocused && (
                                        <div className="absolute w-full mt-1 bg-white border border-[#eaeff4] rounded-lg shadow-lg overflow-y-auto z-10">
                                            {suggestions.map((stock: { ticker_symbol: string; name: string; market_cap: number; logo_url: string; ticker_id: string; exchange: string; company_id: string; }, index: number) => (
                                                <button
                                                    key={stock.ticker_symbol}
                                                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${index === activeIndex ? 'bg-green-100' : ''}`}
                                                    onClick={() => handleSelectStock(stock)}
                                                    role="option"
                                                    aria-selected={index === activeIndex}
                                                >
                                                    <div className="flex items-center">
                                                        {stock.logo_url && (
                                                            <img
                                                                src={stock.logo_url}
                                                                alt={stock.name}
                                                                className="w-6 h-6 mr-2"
                                                            />
                                                        )}
                                                        <div>
                                                            <div className="font-medium text-gray-900 text-xs">{stock.ticker_symbol}</div>
                                                            <div className="text-gray-600">
                                                                <span className="font-bold text-gray-900 text-xs">{stock.name}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}
export default TickerSearchModal;
